<template>
  <div class="payment-receipt">
    <!-- Header com Logo -->
    <div class="receipt-header">
      <div class="logo-container">
        <img
          v-if="paylink.system.logo_url"
          :src="paylink.system.logo_url"
          :alt="paylink.system.app_name"
          class="company-logo"
        >
        <h1 class="company-name">{{ paylink.system.app_name }}</h1>
      </div>
      <div class="receipt-title">
        <h2>Comprovante de Pagamento</h2>
        <div class="status-badge success">
          <v-icon color="white" small>check_circle</v-icon>
          <span>Pagamento Aprovado</span>
        </div>
      </div>
    </div>

    <!-- Informações do Pagamento -->
    <div class="receipt-content">
      <v-card class="receipt-card" outlined>
        <v-card-text class="pa-6">
          <div class="receipt-grid">
            <!-- Status -->
            <div class="receipt-item">
              <div class="item-label">
                <v-icon small class="mr-2">info</v-icon>
                Status
              </div>
              <div class="item-value success-text">
                {{ paymentResult.status || 'Aprovado' }}
              </div>
            </div>

            <!-- Mensagem -->
            <div class="receipt-item">
              <div class="item-label">
                <v-icon small class="mr-2">message</v-icon>
                Mensagem
              </div>
              <div class="item-value">
                {{ paymentResult.message || 'Pagamento realizado com sucesso!' }}
              </div>
            </div>

            <!-- Valor -->
            <div class="receipt-item">
              <div class="item-label">
                <v-icon small class="mr-2">monetization_on</v-icon>
                Valor
              </div>
              <div class="item-value amount">
                R$ {{ formatCurrency(paymentResult.amount || paylink.amount) }}
              </div>
            </div>

            <!-- Tipo -->
            <div class="receipt-item">
              <div class="item-label">
                <v-icon small class="mr-2">credit_card</v-icon>
                Tipo
              </div>
              <div class="item-value">
                {{ paymentResult.installments > 1 ? 'Crédito Parcelado' : 'Crédito à Vista' }}
              </div>
            </div>

            <!-- Parcelas -->
            <div class="receipt-item">
              <div class="item-label">
                <v-icon small class="mr-2">credit_card_multiple</v-icon>
                Parcelas
              </div>
              <div class="item-value">
                {{ paymentResult.installments || 1 }}x
              </div>
            </div>

            <!-- Identificador -->
            <div class="receipt-item">
              <div class="item-label">
                <v-icon small class="mr-2">fingerprint</v-icon>
                Identificador
              </div>
              <div class="item-value code">
                {{ paymentResult.uuid || paylink.uuid }}
              </div>
            </div>

            <!-- Código de Autorização -->
            <div class="receipt-item">
              <div class="item-label">
                <v-icon small class="mr-2">receipt</v-icon>
                Código de Autorização
              </div>
              <div class="item-value code">
                {{ paymentResult.authorization_code || 'N/A' }}
              </div>
            </div>

            <!-- Vencimento -->
            <div class="receipt-item">
              <div class="item-label">
                <v-icon small class="mr-2">event</v-icon>
                Vencimento
              </div>
              <div class="item-value">
                {{ formatDateSafe(paymentResult.due_on || paylink.duedate, paymentResult.due_date || paylink) }}
              </div>
            </div>

            <!-- Pago em -->
            <div class="receipt-item">
              <div class="item-label">
                <v-icon small class="mr-2">schedule</v-icon>
                Pago em
              </div>
              <div class="item-value">
                {{ formatDateTime(new Date()) }}
              </div>
            </div>

            <!-- Descrição -->
            <div class="receipt-item full-width">
              <div class="item-label">
                <v-icon small class="mr-2">description</v-icon>
                Descrição
              </div>
              <div class="item-value">
                {{ paymentResult.description || paylink.description }}
              </div>
            </div>

            <!-- Cliente -->
            <div class="receipt-item full-width">
              <div class="item-label">
                <v-icon small class="mr-2">person</v-icon>
                Cliente
              </div>
              <div class="item-value">
                {{ paylink.name }}
              </div>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </div>

    <!-- Ações -->
    <div class="receipt-actions">
      <v-btn
        color="primary"
        outlined
        @click="printReceipt"
        class="mr-4"
      >
        <v-icon left>print</v-icon>
        Imprimir
      </v-btn>
      


      <v-btn
        color="grey darken-1"
        outlined
        @click="closeReceipt"
      >
        <v-icon left>close</v-icon>
        Fechar
      </v-btn>
    </div>

    <!-- Informações Adicionais -->
    <div class="receipt-footer">
      <v-alert
        type="info"
        outlined
        dense
      >
        <div class="text-center">
          <v-icon color="info" size="20" class="mr-2">info</v-icon>
          <span class="text-caption">
            Este comprovante serve como recibo oficial do pagamento. 
            Guarde-o para sua referência.
          </span>
        </div>
      </v-alert>
    </div>
  </div>
</template>

<script>
export default {
  name: "PaymentReceipt",
  props: {
    paylink: {
      type: Object,
      required: true,
    },
    paymentResult: {
      type: Object,
      required: true,
    },
  },

  mounted() {
    // Aplicar branding do parceiro com delay para garantir que o componente esteja pronto
    this.$nextTick(() => {
      setTimeout(() => {
        this.updatePageBranding();
        this.updatePageColors();
      }, 100);
    });
  },

  beforeDestroy() {
    // Restaurar configurações padrão ao sair
    document.title = "FlowIt";
    
    // Limpar propriedades CSS customizadas
    document.documentElement.style.removeProperty('--paylink-primary-color');
    document.documentElement.style.removeProperty('--paylink-secondary-color');
    document.documentElement.style.removeProperty('--paylink-primary-transparent');
    document.documentElement.style.removeProperty('--paylink-primary-dark');
  },

  methods: {
    formatCurrency(value) {
      return parseFloat(value).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    formatDate(date) {
      if (!date) return 'N/A';
      
      // Se o valor já está em formato brasileiro (display), usar diretamente
      if (typeof date === 'string' && date.includes('/')) {
        return date;
      }
      
      // Caso contrário, tentar converter
      try {
        return new Date(date).toLocaleDateString('pt-BR');
      } catch (error) {
        console.warn('Erro ao formatar data:', date, error);
        return date || 'N/A';
      }
    },

    formatDateSafe(legacyDate, apiDateObject) {
      // Primeiro, tentar usar os campos padronizados da API
      if (apiDateObject && typeof apiDateObject === 'object') {
        // Se tem campo display (formato brasileiro), usar diretamente
        if (apiDateObject.display) {
          return apiDateObject.display;
        }
        
        // Se tem campo iso, converter para formato brasileiro
        if (apiDateObject.iso) {
          try {
            return new Date(apiDateObject.iso).toLocaleDateString('pt-BR');
          } catch (error) {
            console.warn('Erro ao converter data ISO:', apiDateObject.iso, error);
          }
        }
        
        // Se tem timestamp, converter para formato brasileiro
        if (apiDateObject.timestamp) {
          try {
            return new Date(apiDateObject.timestamp * 1000).toLocaleDateString('pt-BR');
          } catch (error) {
            console.warn('Erro ao converter timestamp:', apiDateObject.timestamp, error);
          }
        }
      }
      
      // Fallback para método legacy (pode causar Invalid Date)
      return this.formatDate(legacyDate);
    },

    formatDateTime(date) {
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    printReceipt() {
      window.print();
    },



    closeReceipt() {
      this.$emit('close-receipt');
    },

    updatePageBranding() {
      if (!this.paylink) {
        console.log('Paylink não disponível para branding');
        return;
      }

      console.log('Aplicando branding do parceiro:', this.paylink.system);

      // Atualizar título do documento com nome do parceiro
      const partnerName = this.paylink.account.fantasy_name || this.paylink.account.name;
      document.title = `Comprovante - ${partnerName}`;
      console.log('Título atualizado:', document.title);

      // Atualizar favicon se logo do parceiro estiver disponível
      if (this.paylink.system.logo_url) {
        console.log('Logo URL encontrada:', this.paylink.system.logo_url);
        this.updateFavicon(this.paylink.system.logo_url);
      } else {
        console.log('Logo URL não encontrada no sistema');
      }
    },

    updateFavicon(logoUrl) {
      try {
        // Remover todos os favicons existentes
        const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
        existingFavicons.forEach(favicon => favicon.remove());

        // Remover apple-touch-icon existente
        const existingAppleIcon = document.querySelector('link[rel="apple-touch-icon"]');
        if (existingAppleIcon) {
          existingAppleIcon.remove();
        }

        // Adicionar novo favicon com logo do parceiro
        const favicon = document.createElement('link');
        favicon.rel = 'icon';
        favicon.type = 'image/png';
        favicon.href = logoUrl;
        favicon.sizes = '32x32';
        document.head.appendChild(favicon);

        // Adicionar favicon para diferentes tamanhos
        const favicon16 = document.createElement('link');
        favicon16.rel = 'icon';
        favicon16.type = 'image/png';
        favicon16.href = logoUrl;
        favicon16.sizes = '16x16';
        document.head.appendChild(favicon16);

        const favicon32 = document.createElement('link');
        favicon32.rel = 'icon';
        favicon32.type = 'image/png';
        favicon32.href = logoUrl;
        favicon32.sizes = '32x32';
        document.head.appendChild(favicon32);

        // Adicionar apple-touch-icon
        const appleIcon = document.createElement('link');
        appleIcon.rel = 'apple-touch-icon';
        appleIcon.href = logoUrl;
        appleIcon.sizes = '180x180';
        document.head.appendChild(appleIcon);

        // Forçar atualização do favicon
        const link = document.createElement('link');
        link.rel = 'icon';
        link.href = logoUrl + '?v=' + new Date().getTime();
        document.head.appendChild(link);

        console.log('Favicon atualizado com sucesso:', logoUrl);
      } catch (error) {
        console.error('Erro ao atualizar favicon:', error);
      }
    },

    updatePageColors() {
      if (!this.paylink || !this.paylink.system.primary_color) return;

      const primaryColor = this.paylink.system.primary_color;
      const secondaryColor = this.paylink.system.secondary_color || '#ffffff';

      // Criar versões transparentes da cor primária
      const primaryColorTransparent = this.hexToRgba(primaryColor, 0.1);
      const darkerShadeTransparent = this.hexToRgba(this.darkenColor(primaryColor, 20), 0.05);
      const primaryColorDark = this.darkenColor(primaryColor, 30);

      // Aplicar propriedades CSS customizadas para theming dinâmico
      document.documentElement.style.setProperty('--paylink-primary-color', primaryColor);
      document.documentElement.style.setProperty('--paylink-secondary-color', secondaryColor);
      document.documentElement.style.setProperty('--paylink-primary-transparent', primaryColorTransparent);
      document.documentElement.style.setProperty('--paylink-primary-dark', primaryColorDark);

      // Atualizar o background do container do comprovante com gradiente da empresa
      const receiptContainer = document.querySelector('.receipt-container');
      if (receiptContainer) {
        receiptContainer.style.background = `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColorDark} 100%)`;
      }
    },

    darkenColor(color, percent) {
      // Converter hex para RGB
      const hex = color.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);

      // Escurecer cada componente
      const darkenedR = Math.max(0, Math.floor(r * (100 - percent) / 100));
      const darkenedG = Math.max(0, Math.floor(g * (100 - percent) / 100));
      const darkenedB = Math.max(0, Math.floor(b * (100 - percent) / 100));

      // Converter de volta para hex
      const toHex = (n) => {
        const hex = n.toString(16);
        return hex.length === 1 ? '0' + hex : hex;
      };

      return `#${toHex(darkenedR)}${toHex(darkenedG)}${toHex(darkenedB)}`;
    },

    hexToRgba(hex, alpha) {
      // Converter hex para RGB
      const hexColor = hex.replace('#', '');
      const r = parseInt(hexColor.substr(0, 2), 16);
      const g = parseInt(hexColor.substr(2, 2), 16);
      const b = parseInt(hexColor.substr(4, 2), 16);
      
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
  },
};
</script>

<style scoped>
.payment-receipt {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  background: #ffffff;
  min-height: 100vh;
  border-radius: 16px;
}

.receipt-header {
  text-align: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #f5f5f5;
}

.logo-container {
  margin-bottom: 20px;
}

.company-logo {
  max-height: 80px;
  max-width: 200px;
  object-fit: contain;
  margin-bottom: 10px;
}

.company-name {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.receipt-title {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.receipt-title h2 {
  font-size: 2rem;
  font-weight: 700;
  color: var(--paylink-primary-color, #1976d2);
  margin: 0;
}

.status-badge {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.9rem;
}

.status-badge.success {
  background: var(--paylink-primary-color, #4caf50);
  color: white;
}

.receipt-content {
  margin-bottom: 30px;
}

.receipt-card {
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.receipt-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.receipt-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.receipt-item.full-width {
  grid-column: 1 / -1;
}

.item-label {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  font-weight: 500;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.item-value {
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  word-break: break-word;
}

.item-value.amount {
  font-size: 1.25rem;
  color: var(--paylink-primary-color, #4caf50);
}

.item-value.code {
  font-family: 'Courier New', monospace;
  background: #f5f5f5;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.875rem;
}

.item-value.success-text {
  color: var(--paylink-primary-color, #4caf50);
}

.receipt-actions {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-bottom: 30px;
}

.receipt-footer {
  margin-top: 20px;
}

/* Print Styles */
@media print {
  .payment-receipt {
    padding: 0;
    max-width: none;
  }

  .receipt-actions {
    display: none;
  }

  .receipt-footer {
    display: none;
  }

  .receipt-card {
    box-shadow: none;
    border: 1px solid #ddd;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .receipt-grid {
    grid-template-columns: 1fr;
  }

  .receipt-actions {
    flex-direction: column;
    align-items: center;
  }

  .receipt-actions .v-btn {
    width: 100%;
    max-width: 300px;
  }
}
</style> 